import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { resourcesPanelStyles as styles } from './ResourcesPanel.styles';
import {
  RESOURCE_PANEL_SUBTITLE,
  RESOURCE_PANEL_TITLE,
  RESOURCES,
} from './resourceList';

interface ResourcesPanelProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Slide-in panel listing every data source / reference used by the tool.
 * Opens from the right on desktop and up from the bottom on mobile; closes on
 * the ✕, the backdrop, or Escape. Each "Learn more" opens the source in a new
 * tab.
 */
export function ResourcesPanel({ open, onClose }: ResourcesPanelProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: styles.paper } }}
    >
      <Box sx={styles.header}>
        <Box sx={styles.headerText}>
          <Typography component="h2" sx={styles.title}>
            {RESOURCE_PANEL_TITLE}
          </Typography>
          <Typography sx={styles.subtitle}>{RESOURCE_PANEL_SUBTITLE}</Typography>
        </Box>
        <IconButton aria-label="Close resources" onClick={onClose} sx={styles.close}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={styles.list}>
        {RESOURCES.map((resource, index) => (
          <Box key={resource.title}>
            <Box component="article" sx={styles.item}>
              <Typography component="h3" sx={styles.itemTitle}>
                {resource.title}
              </Typography>
              <Typography sx={styles.itemYear}>{resource.year}</Typography>
              <Typography sx={styles.itemDesc}>{resource.description}</Typography>
              <Link
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={styles.learnMore}
                underline="none"
              >
                Learn more <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </Link>
            </Box>
            {index < RESOURCES.length - 1 && <Divider />}
          </Box>
        ))}
      </Box>
    </Drawer>
  );
}
